import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, useTheme } from '@mui/material';

const Terms: React.FC = () => {
  const theme = useTheme();

  const sectionHeading = {
    fontSize: '24px',
    fontWeight: 700,
    mb: 2,
    color: theme.palette.text.primary,
  } as const;

  const body = {
    fontSize: '16px',
    lineHeight: 1.7,
    color: theme.palette.text.secondary,
    mb: 2,
  } as const;

  return (
    <Box sx={{ maxWidth: '800px', mx: 'auto', px: 3, py: 4 }}>
      {/* Hero */}
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '36px', sm: '48px' },
            fontWeight: 700,
            lineHeight: 1.1,
            mb: 2,
            color: theme.palette.text.primary,
            letterSpacing: '-0.02em',
          }}
        >
          Terms of Use
        </Typography>
        <Typography sx={{ fontSize: '18px', color: theme.palette.text.secondary, fontWeight: 500 }}>
          The ground rules for using emotify. Last updated: July 2026.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <Box>
          <Typography variant="h5" sx={sectionHeading}>Acceptance</Typography>
          <Typography sx={body}>
            By using emotify, you agree to these terms. If you don't agree with them, please don't use the tool.
            emotify is a free, hobby project offered as-is.
          </Typography>
        </Box>

        <Box>
          <Typography variant="h5" sx={sectionHeading}>What emotify does</Typography>
          <Typography sx={body}>
            emotify takes a link to a public Spotify, YouTube Music or Apple Music playlist and generates a visual heatmap of
            its emotional characteristics, based on per-track audio features. It is provided for personal,
            non-commercial enjoyment and curiosity. It requires no account and does not connect to your streaming
            profile.
          </Typography>
        </Box>

        <Box>
          <Typography variant="h5" sx={sectionHeading}>Acceptable use</Typography>
          <Typography sx={body}>
            Please use emotify only with playlists that are public and that you have the right to view. Don't use
            the tool to break the law, to abuse or overload the service (for example, automated bulk requests or
            scraping), to reverse-engineer or disrupt it, or to misrepresent its output as something it isn't.
          </Typography>
        </Box>

        <Box>
          <Typography variant="h5" sx={sectionHeading}>Third-party content and trademarks</Typography>
          <Typography sx={body}>
            Playlist data, track metadata, and album artwork belong to their respective owners and to the
            streaming platforms. "Spotify", "YouTube Music" and "Apple Music" are trademarks of their respective companies;
            emotify is an independent project and is not affiliated with, endorsed by, or sponsored by either.
            Their marks are used only to indicate the source of a playlist.
          </Typography>
        </Box>

        <Box>
          <Typography variant="h5" sx={sectionHeading}>Accuracy and "as-is" disclaimer</Typography>
          <Typography sx={body}>
            The emotional labels and heatmaps emotify produces are an interpretive, entertainment-oriented
            estimate derived from audio-feature data — not a scientific or authoritative judgment about any song
            or artist. The service is provided "as is" and "as available", without warranties of any kind, and we
            don't guarantee it will be accurate, uninterrupted, or error-free.
          </Typography>
        </Box>

        <Box>
          <Typography variant="h5" sx={sectionHeading}>Limitation of liability</Typography>
          <Typography sx={body}>
            To the fullest extent permitted by law, emotify and its creator are not liable for any damages
            arising from your use of, or inability to use, the tool.
          </Typography>
        </Box>

        <Box>
          <Typography variant="h5" sx={sectionHeading}>Advertising</Typography>
          <Typography sx={body}>
            emotify is supported by advertising. See our{' '}
            <Link to="/privacy" style={{ color: theme.palette.primary.main, fontWeight: 600 }}>Privacy Policy</Link>{' '}
            for how ads and cookies are handled.
          </Typography>
        </Box>

        <Box>
          <Typography variant="h5" sx={sectionHeading}>Changes and contact</Typography>
          <Typography sx={body}>
            We may update these terms over time; the "last updated" date above reflects the latest version.
            Questions? Reach us via the{' '}
            <Link to="/contact" style={{ color: theme.palette.primary.main, fontWeight: 600 }}>Contact page</Link>{' '}
            or at{' '}
            <Typography
              component="a"
              href="mailto:emotify.dev@gmail.com"
              sx={{ color: theme.palette.primary.main, fontWeight: 600, textDecoration: 'none' }}
            >
              emotify.dev@gmail.com
            </Typography>
            .
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Terms;
